import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AdviceResponseElement,
  AppScenariosAppointmentBasePayloadAppointmentInResponse,
  AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO,
  BasicsModel,
  FamilyHistorySectionModel,
  FemaleSexHormonesModel,
  GetMemberResponse,
  GoalsSectionModel,
  HealthLifestyleModel,
  HealthSummaryResponseElement,
  ListDietDiaryResponseElement,
  ListNoteResponseElement
} from '../types/apiGeneratedTypes';

export type OpenNotesModalPayload = {
  intent?: 'create' | 'update' | 'view';
  forceViewOnly?: boolean;
  isReferralLetter?: boolean;
  noteDataToEdit?: ListNoteResponseElement | null;
};

export interface MembersPageState {
  memberData: GetMemberResponse | null;
  appointmentsList: AppScenariosAppointmentBasePayloadAppointmentInResponse[];
  appointmentsListForTestsResults: AppScenariosAppointmentBasePayloadAppointmentInResponse[];
  appointmentTestResults: any[];
  memberBiomarkersTestResultsList:
    | AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO[]
    | null;
  memberBiomarkersExistingIdsByAppointment: number[] | null;
  limit: number;
  appointmentIdToEdit: number | null;
  healthSummaryIdToEdit: number | null;
  medicalCard: {
    basics?: BasicsModel | null;
    health_lifestyle?: HealthLifestyleModel | null;
    nutritionist_questions?: any | null;
    family_history?: FamilyHistorySectionModel | null;
    female_sex_hormones?: FemaleSexHormonesModel | null;
    goals?: GoalsSectionModel | null;
    scan_info?: Record<string, unknown> | null;
    created_at?: string | null;
  } | null;
  dietDiary: ListDietDiaryResponseElement[] | null;
  healthSummaryList: HealthSummaryResponseElement[] | null;
  healthSummaryDataToEdit: HealthSummaryResponseElement | null;
  selectedAppointmentIdForTestResults: any;
  testResultIdToDelete: any;
  adviceIdToDelete: number | null;
  adviceIdToEdit: number | null;
  adviceList: AdviceResponseElement[] | null;
  notesList: ListNoteResponseElement[] | null;
  noteDataToEdit: ListNoteResponseElement | null;
  testResultsModalMode: 'create' | 'update';
  memberBiomarkersResultsByAppointment:
    | AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO[]
    | null;
  selectedBiomarkerResult: AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO | null;
  selectedAppointmentIdToView: number | null;
  // Modals
  isViewAppointmentResultsModalOpened: boolean;
  isShowBiomarkerTestResultModalOpened: boolean;
  isAddAppointmentModalOpened: boolean;
  isDeleteAppointmentModalOpened: boolean;
  isDeleteAdviceModalOpened: boolean;
  isAssignDoctorModalOpened: boolean;
  assignedDoctorId: string | null;
  assignedNutritionistId: string | null;
  isAssignNutritionistModalOpened: boolean;
  assignedInchargeAdminId: string | null;
  isAssignInchargeModalOpened: boolean;
  isMedicalCardModalOpened: boolean;
  isAddressModalOpened: boolean;
  isHealthSummaryModalOpened: boolean;
  isAddTestResultsModalOpened: boolean;
  isDeleteTestResultsModalOpened: boolean;
  isViewTestResultsModalOpened: boolean;
  isAdviceModalOpened: boolean;
  isNotesModalOpened: boolean;
  notesModalIntent: 'create' | 'update' | 'view';
  notesModalForceViewOnly: boolean;
  notesModalIsReferralLetter: boolean;
  isDeleteNoteModalOpened: boolean;
  isSaveDataNoteModalOpened: boolean;
  appointmentIdToEditForTestResults: string | null;
  isSelectHL7ModalOpened: boolean;
  biologicalAge: number | null | string;
  biomarkerAppointmentsList: string[];
  biomarkerAppointmentDateFilter: string;
  biomarkerResultsSearchTerm: string;
  biomarkerResultsBodySystem: string;
}

export const initialState: MembersPageState = {
  memberData: null,
  appointmentsList: [],
  appointmentsListForTestsResults: [],
  appointmentTestResults: [],
  memberBiomarkersResultsByAppointment: [],
  memberBiomarkersExistingIdsByAppointment: [],
  selectedAppointmentIdToView: null,
  selectedAppointmentIdForTestResults: null,
  limit: 7,
  isAddAppointmentModalOpened: false,
  isAssignNutritionistModalOpened: false,
  assignedInchargeAdminId: null,
  isAssignInchargeModalOpened: false,
  appointmentIdToEdit: null,
  isDeleteAppointmentModalOpened: false,
  isAssignDoctorModalOpened: false,
  assignedDoctorId: null,
  assignedNutritionistId: null,
  isMedicalCardModalOpened: false,
  isHealthSummaryModalOpened: false,
  isAddressModalOpened: false,
  medicalCard: null,
  dietDiary: null,
  healthSummaryList: null,
  healthSummaryIdToEdit: null,
  healthSummaryDataToEdit: null,
  testResultIdToDelete: null,
  adviceIdToDelete: null,
  adviceIdToEdit: null,
  adviceList: null,
  memberBiomarkersTestResultsList: null,
  notesList: null,
  noteDataToEdit: null,
  testResultsModalMode: 'create',
  selectedBiomarkerResult: null,
  isAddTestResultsModalOpened: false,
  isDeleteTestResultsModalOpened: false,
  isViewTestResultsModalOpened: false,
  isAdviceModalOpened: false,
  isDeleteAdviceModalOpened: false,
  isNotesModalOpened: false,
  notesModalIntent: 'create',
  notesModalForceViewOnly: false,
  notesModalIsReferralLetter: false,
  isDeleteNoteModalOpened: false,
  isSaveDataNoteModalOpened: false,
  isShowBiomarkerTestResultModalOpened: false,
  isViewAppointmentResultsModalOpened: false,
  appointmentIdToEditForTestResults: null,
  isSelectHL7ModalOpened: false,
  biologicalAge: null,
  biomarkerAppointmentsList: [],
  biomarkerAppointmentDateFilter: '',
  biomarkerResultsSearchTerm: '',
  biomarkerResultsBodySystem: ''
};

const memberPageSlice = createSlice({
  name: 'member',
  initialState,
  reducers: {
    resetMemberStore: () => initialState,
    setMemberData(state, action: PayloadAction<GetMemberResponse>) {
      state.memberData = action.payload;
    },
    setMemberMedicalCard(state, action) {
      state.medicalCard = action.payload;
    },
    setAppointmentsList(
      state,
      action: PayloadAction<
        AppScenariosAppointmentBasePayloadAppointmentInResponse[]
      >
    ) {
      state.appointmentsList = action.payload;
    },
    setAppointmentIdToEditForTestResults(
      state,
      action: PayloadAction<string | null>
    ) {
      state.appointmentIdToEditForTestResults = action.payload;
    },
    setAppointmentsForTestResultsList(
      state,
      action: PayloadAction<
        AppScenariosAppointmentBasePayloadAppointmentInResponse[]
      >
    ) {
      state.appointmentsListForTestsResults = action.payload;
    },
    setHealthSummariesList(
      state,
      action: PayloadAction<HealthSummaryResponseElement[]>
    ) {
      state.healthSummaryList = action.payload;
    },
    appendAppointmentsList(state, action: PayloadAction<any[]>) {
      if (!state.appointmentsList) {
        state.appointmentsList = action.payload;
      } else {
        state.appointmentsList = [...state.appointmentsList, ...action.payload];
      }
    },
    appendHealthSummariesList(
      state,
      action: PayloadAction<HealthSummaryResponseElement[]>
    ) {
      if (!state.healthSummaryList) {
        state.healthSummaryList = action.payload;
      } else {
        state.healthSummaryList = [
          ...state.healthSummaryList,
          ...action.payload
        ];
      }
    },
    appendDietDiaryList(
      state,
      action: PayloadAction<ListDietDiaryResponseElement[]>
    ) {
      if (!state.dietDiary) {
        state.dietDiary = action.payload;
      } else {
        state.dietDiary = [...state.dietDiary, ...action.payload];
      }
    },
    changeModalVisibility(state, action: PayloadAction<boolean>) {
      state.isAddAppointmentModalOpened = action.payload;
    },
    changeNotesModalVisibility(state, action: PayloadAction<boolean>) {
      state.isNotesModalOpened = action.payload;
      if (!action.payload) {
        state.noteDataToEdit = null;
        state.notesModalIntent = 'create';
        state.notesModalForceViewOnly = false;
        state.notesModalIsReferralLetter = false;
      }
    },
    openNotesModal(
      state,
      action: PayloadAction<OpenNotesModalPayload | undefined>
    ) {
      const options = action.payload ?? {};
      state.isNotesModalOpened = true;
      state.notesModalIntent = options.intent ?? 'create';
      state.notesModalForceViewOnly = options.forceViewOnly ?? false;
      state.notesModalIsReferralLetter = options.isReferralLetter ?? false;
      state.noteDataToEdit = options.noteDataToEdit ?? null;
    },
    changeDeleteNoteModalVisibility(state, action: PayloadAction<boolean>) {
      state.isDeleteNoteModalOpened = action.payload;
    },
    changeSaveDataNoteModalVisibility(state, action: PayloadAction<boolean>) {
      state.isSaveDataNoteModalOpened = action.payload;
    },
    setNotesList(state, action: PayloadAction<any[]>) {
      state.notesList = action.payload;
    },
    changeDeleteModalVisibility(state, action: PayloadAction<boolean>) {
      state.isDeleteAppointmentModalOpened = action.payload;
    },
    changeAssignDoctorModalVisibility(state, action: PayloadAction<boolean>) {
      state.isAssignDoctorModalOpened = action.payload;
    },
    changeAssignedDoctorId(state, action: PayloadAction<string | null>) {
      state.assignedDoctorId = action.payload;
    },
    changeAssignedNutritionistId(state, action: PayloadAction<string | null>) {
      state.assignedNutritionistId = action.payload;
    },
    changeAssignNutritionistModalVisibility(
      state,
      action: PayloadAction<boolean>
    ) {
      state.isAssignNutritionistModalOpened = action.payload;
    },
    changeAssignedInchargeAdminId(state, action: PayloadAction<string | null>) {
      state.assignedInchargeAdminId = action.payload;
    },
    changeAssignInchargeModalVisibility(state, action: PayloadAction<boolean>) {
      state.isAssignInchargeModalOpened = action.payload;
    },
    changeMedicalCardModalVisibility(state, action: PayloadAction<boolean>) {
      state.isMedicalCardModalOpened = action.payload;
    },
    changeHealthSummaryModalVisibility(state, action: PayloadAction<boolean>) {
      state.isHealthSummaryModalOpened = action.payload;
    },
    changeAddressModalVisibility(state, action: PayloadAction<boolean>) {
      state.isAddressModalOpened = action.payload;
    },
    setAppointmentIdToEdit(state, action: PayloadAction<number | null>) {
      state.appointmentIdToEdit = action.payload;
    },
    setHealthSummaryIdToEdit(state, action: PayloadAction<number | null>) {
      state.healthSummaryIdToEdit = action.payload;
    },
    setHealthSummaryDataToEdit(
      state,
      action: PayloadAction<HealthSummaryResponseElement | null>
    ) {
      state.healthSummaryDataToEdit = action.payload;
    },
    setMemberDietDiary(
      state,
      action: PayloadAction<ListDietDiaryResponseElement[]>
    ) {
      state.dietDiary = action.payload;
    },
    setUpdatedAppointment(state, action: PayloadAction<any>) {
      state.appointmentsList = state.appointmentsList.map((appointment) => {
        if (appointment.id === action.payload.appointment_id) {
          return action.payload;
        }
        return appointment;
      });
    },
    updateHealthSummaryStore(state, action: PayloadAction<any>) {
      if (state.healthSummaryList) {
        state.healthSummaryList = state.healthSummaryList?.map((summary) => {
          if (summary.id === action.payload.healthSummaryIdToEdit) {
            return {
              ...summary,
              details: action.payload.note
            };
          }
          return summary;
        });
      }
    },
    updateMemberAddress(state, action: PayloadAction<any>) {
      if (state.memberData) {
        state.memberData = {
          ...state.memberData,
          address_line_1: action.payload.address_line_1,
          address_line_2: action.payload.address_line_2,
          city: action.payload.city,
          post_code: action.payload.post_code,
          country: action.payload.country
        };
      }
    },
    setMemberBiomarkersTestResultsList(
      state,
      action: PayloadAction<
        AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO[]
      >
    ) {
      state.memberBiomarkersTestResultsList = action.payload;
    },
    changeTestResultsModalVisibility(state, action: PayloadAction<boolean>) {
      state.isAddTestResultsModalOpened = action.payload;
    },
    setSelectedAppointmentIdForTestResults(state, action: PayloadAction<any>) {
      state.selectedAppointmentIdForTestResults = action.payload;
    },
    setTestResultIdToDelete(state, action: PayloadAction<any>) {
      state.testResultIdToDelete = action.payload;
    },
    setAdviceIdToDelete(state, action: PayloadAction<any>) {
      state.adviceIdToDelete = action.payload;
    },
    setAdviceIdToEdit(state, action: PayloadAction<any>) {
      state.adviceIdToEdit = action.payload;
    },
    changeDeleteTestResultModalVisibility(
      state,
      action: PayloadAction<boolean>
    ) {
      state.isDeleteTestResultsModalOpened = action.payload;
    },
    changeDeleteAdviceModalVisibility(state, action: PayloadAction<boolean>) {
      state.isDeleteAdviceModalOpened = action.payload;
    },
    changeViewTestResultModalVisibility(state, action: PayloadAction<boolean>) {
      state.isViewTestResultsModalOpened = action.payload;
    },
    changeCreateAdviceModalVisibility(state, action: PayloadAction<boolean>) {
      state.isAdviceModalOpened = action.payload;
    },
    setMemberAdvicesList(
      state,
      action: PayloadAction<AdviceResponseElement[]>
    ) {
      state.adviceList = action.payload;
    },
    setNoteDataToEdit(state, action: PayloadAction<any | null>) {
      state.noteDataToEdit = action.payload;
    },
    setTestResultsModalMode(state, action: PayloadAction<'create' | 'update'>) {
      state.testResultsModalMode = action.payload;
    },
    setMemberBiomarkersResultsByAppointment(
      state,
      action: PayloadAction<number[]>
    ) {
      state.memberBiomarkersExistingIdsByAppointment = action.payload;
    },
    setSelectedBiomarkerResult(
      state,
      action: PayloadAction<AppScenariosBiomarkerGetMemberResultsApplicationPayloadBiomarkerWithResultDTO | null>
    ) {
      state.selectedBiomarkerResult = action.payload;
    },
    setShowBiomarkerTestResultModalVisibility(
      state,
      action: PayloadAction<boolean>
    ) {
      state.isShowBiomarkerTestResultModalOpened = action.payload;
    },
    setIsSelectHL7ModalOpened(state, action: PayloadAction<boolean>) {
      state.isSelectHL7ModalOpened = action.payload;
    },
    setBiologicalAge(state, action: PayloadAction<number | null | string>) {
      state.biologicalAge = action.payload;
    },

    setSelectedAppointmentIdToView(
      state,
      action: PayloadAction<number | null>
    ) {
      state.selectedAppointmentIdToView = action.payload;
    },
    changeViewAppointmentResultsModalVisibility(
      state,
      action: PayloadAction<boolean>
    ) {
      state.isViewAppointmentResultsModalOpened = action.payload;
    },
    setAppointmentsForBiomarkers(
      state,
      action: PayloadAction<string[]>
    ) {
      state.biomarkerAppointmentsList = action.payload;
    },
    appendAppointmentsForBiomarkers(
      state,
      action: PayloadAction<string[]>
    ) {
      state.biomarkerAppointmentsList = [
        ...new Set([...state.biomarkerAppointmentsList, ...action.payload])
      ];
    },
    setBiomarkerAppointmentDateFilter(state, action: PayloadAction<string>) {
      state.biomarkerAppointmentDateFilter = action.payload;
    },
    setBiomarkerResultsSearchTerm(state, action: PayloadAction<string>) {
      state.biomarkerResultsSearchTerm = action.payload;
    },
    setBiomarkerResultsBodySystem(state, action: PayloadAction<string>) {
      state.biomarkerResultsBodySystem = action.payload;
    }
  }
});

export const {
  setMemberData,
  setAppointmentsList,
  appendAppointmentsList,
  changeModalVisibility,
  setAppointmentIdToEdit,
  setUpdatedAppointment,
  changeDeleteModalVisibility,
  changeAssignDoctorModalVisibility,
  changeAssignedDoctorId,
  changeAssignedNutritionistId,
  changeAssignNutritionistModalVisibility,
  changeAssignedInchargeAdminId,
  changeAssignInchargeModalVisibility,
  setMemberMedicalCard,
  changeMedicalCardModalVisibility,
  setMemberDietDiary,
  appendDietDiaryList,
  setHealthSummariesList,
  changeHealthSummaryModalVisibility,
  appendHealthSummariesList,
  setHealthSummaryIdToEdit,
  setHealthSummaryDataToEdit,
  updateHealthSummaryStore,
  changeAddressModalVisibility,
  updateMemberAddress,
  setAppointmentsForTestResultsList,
  setMemberBiomarkersTestResultsList,
  changeTestResultsModalVisibility,
  setSelectedAppointmentIdForTestResults,
  setTestResultIdToDelete,
  changeDeleteTestResultModalVisibility,
  changeViewTestResultModalVisibility,
  resetMemberStore,
  setMemberAdvicesList,
  changeCreateAdviceModalVisibility,
  setAdviceIdToDelete,
  changeDeleteAdviceModalVisibility,
  setAdviceIdToEdit,
  changeNotesModalVisibility,
  openNotesModal,
  changeDeleteNoteModalVisibility,
  setNotesList,
  setNoteDataToEdit,
  changeSaveDataNoteModalVisibility,
  setTestResultsModalMode,
  setMemberBiomarkersResultsByAppointment,
  setSelectedBiomarkerResult,
  setShowBiomarkerTestResultModalVisibility,
  setSelectedAppointmentIdToView,
  changeViewAppointmentResultsModalVisibility,
  setAppointmentIdToEditForTestResults,
  setIsSelectHL7ModalOpened,
  setBiologicalAge,
  setAppointmentsForBiomarkers,
  appendAppointmentsForBiomarkers,
  setBiomarkerAppointmentDateFilter,
  setBiomarkerResultsSearchTerm,
  setBiomarkerResultsBodySystem
} = memberPageSlice.actions;
export default memberPageSlice.reducer;
